// Workshop: turns a natural-language request into a valid creation.
// Understand (type) → generate (structured output following the type's schema) → validate (schema + type checks)
// → repair by sending the problems back to the model, at most `maxAttempts` times.
// Provider-independent: the API plugs in an implementation of `Llm` (Anthropic), the tests a fake model.
import { z } from 'zod/v4';
import { CONTENT_SCHEMAS, CREATION_KINDS, type CreationInput, type CreationKind, type GenerateResponse, type Issue } from '@gruvetto/contracts';
import type { Effort, KindContext, KindDef } from './kind.js';
import { KINDS, validateCreation } from './registry.js';

export interface LlmMessage { role: 'user' | 'assistant'; content: string }
export interface LlmRequest { system: string; messages: LlmMessage[]; schema: z.ZodType; effort: Effort; maxTokens: number }
export interface LlmResult { data: unknown; model: string; usage?: { input: number; output: number } }
export interface Llm { complete(req: LlmRequest): Promise<LlmResult> }
/** `malformed`: the reply was not JSON at all; unlike the others, asking again can fix it. */
export type LlmFailure = 'refusal' | 'truncated' | 'invalid' | 'malformed' | 'unavailable';
/** `message` is shown to the player; `detail` (raw provider response) is only for the server logs. */
export class LlmError extends Error {
  constructor(message: string, readonly reason: LlmFailure, readonly detail?: string) { super(message); }
}

/** Request already validated by GenerateBody. */
export interface GenerateRequest {
  kind: CreationKind | 'auto'; prompt: string;
  base?: { kind: CreationKind; name: string; desc?: string; tags?: string[]; content: unknown };
  history: { role: 'user' | 'assistant'; text: string }[];
  feedback?: string;
}

const PLATFORM = `You are the Gruvetto workshop. Gruvetto is a connected-trainer game: the player pedals on their bike, the trainer measures their power (watts) and can impose a resistance (simulated gradient or ERG power).
Players describe in natural language what they want to create; you produce the complete creation in the requested format, ready to play and to share with the community.
General rules:
- Player-visible texts are written in the language of the player's first request (English if it is ambiguous), warm and concise: name (60 characters at most), desc (one or two sentences), tags (1 to 5 lowercase keywords).
- notes: explain in two or three sentences your choices and what the player can ask for to adjust.
- The cyclist's safety comes first: realistic intensities, a warm-up, no prolonged maximal effort.
- Follow the request precisely (duration, level, theme); if it is vague, make a reasonable choice and say so in notes.
- If a current creation is provided, modify it according to the request and keep the rest intact.
- No trademarks, real people or offensive content.`;

const prompts = new Map<CreationKind, string>();
/** System prompt of a type: stable from one request to the next (cached on the provider side). */
export function systemPrompt(kind: CreationKind): string {
  let s = prompts.get(kind);
  if (!s) {
    const k = KINDS[kind] as KindDef;
    const { name, desc, tags, content } = k.ai.example;
    s = `${PLATFORM}\n\nYou are creating: ${k.label.toLowerCase()} (${k.ai.summary}).\n\n${k.ai.brief}\n\nValid example:\n${JSON.stringify({ name, desc, tags, notes: '…', content })}`;
    prompts.set(kind, s);
  }
  return s;
}

const classifySchema = z.object({ kind: z.enum(CREATION_KINDS) });
const CLASSIFY = `${PLATFORM}\n\nClassify the player's request into a creation type:\n${CREATION_KINDS.map(k => `- ${k}: ${KINDS[k].ai.summary}`).join('\n')}`;

/** Schema requested from the model for a type. */
export function outputSchema(kind: CreationKind) {
  const k = KINDS[kind] as KindDef;
  return z.object({
    name: z.string().max(60), desc: z.string().max(400), tags: z.array(z.string().max(24)).max(5),
    notes: z.string().max(800).describe('choices made and possible adjustments, for the player'),
    content: (k.ai.schema ?? CONTENT_SCHEMAS[kind]) as z.ZodType,
  });
}

export async function classify(llm: Llm, prompt: string): Promise<CreationKind> {
  const r = await llm.complete({ system: CLASSIFY, messages: [{ role: 'user', content: prompt }], schema: classifySchema, effort: 'low', maxTokens: 2000 });
  const p = classifySchema.safeParse(r.data);
  if (!p.success) throw new LlmError('Creation type not recognised', 'invalid');
  return p.data.kind;
}

const list = (issues: Issue[]) => issues.map(i => `- ${i.path ? `[${i.path}] ` : ''}${i.message}`).join('\n');

/** Models write `null` for an optional field they leave out; no creation schema has a meaningful null. */
function dropNulls(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(dropNulls);
  if (!v || typeof v !== 'object') return v;
  return Object.fromEntries(Object.entries(v).filter(([, x]) => x !== null).map(([k, x]) => [k, dropNulls(x)]));
}

/** Long replies sometimes close `content` too early: a field of the content found at the root would otherwise be dropped in silence. */
function liftStrays(out: Record<string, unknown>, schema: ReturnType<typeof outputSchema>) {
  const fields = (schema.shape.content as { shape?: Record<string, unknown> }).shape, content = out.content;
  if (!fields || !content || typeof content !== 'object') return out;
  const strays = Object.keys(out).filter(k => !(k in schema.shape) && k in fields && !(k in content));
  return strays.length ? { ...out, content: { ...content, ...Object.fromEntries(strays.map(k => [k, out[k]])) } } : out;
}

export async function generate(llm: Llm, req: GenerateRequest, ctx: KindContext, { maxAttempts = 3 } = {}): Promise<GenerateResponse> {
  const kind = req.base?.kind ?? (req.kind === 'auto' ? await classify(llm, req.prompt) : req.kind);
  const k = KINDS[kind] as KindDef, schema = outputSchema(kind);
  let user = `Player's request: ${req.prompt}`;
  if (req.base) user += `\n\nCurrent creation to modify:\n${JSON.stringify({ name: req.base.name, desc: req.base.desc ?? '', tags: req.base.tags ?? [], content: req.base.content })}`;
  if (req.feedback) user += `\n\nProblems found when trying the creation:\n${req.feedback}`;
  const messages: LlmMessage[] = [...req.history.map(h => ({ role: h.role, content: h.text })), { role: 'user', content: user }];

  let last: { creation: CreationInput | null; issues: Issue[]; notes: string; model: string; facts: GenerateResponse['facts'] } | null = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let r;
    try {
      r = await llm.complete({ system: systemPrompt(kind), messages, schema, effort: k.ai.effort, maxTokens: k.ai.maxTokens });
    } catch (e) {
      if (!(e instanceof LlmError) || e.reason !== 'malformed' || attempt === maxAttempts) throw e;
      messages.push({ role: 'user', content: 'Your reply was not valid JSON (check that every brace and bracket closes exactly once). Return the complete creation as one valid JSON object.' });
      continue;
    }
    const clean = dropNulls(r.data), out = liftStrays((clean && typeof clean === 'object' ? clean : {}) as Record<string, unknown>, schema);
    const v = validateCreation({ kind, name: out.name, desc: out.desc, tags: out.tags, content: out.content }, ctx);
    last = { creation: v.creation, issues: v.issues, notes: typeof out.notes === 'string' ? out.notes : '', model: r.model, facts: v.facts };
    if (v.ok) return { kind, creation: v.creation!, notes: last.notes, issues: v.issues, facts: v.facts, attempts: attempt, model: r.model };
    messages.push({ role: 'assistant', content: JSON.stringify(out) });
    messages.push({ role: 'user', content: `Your proposal cannot be published:\n${list(v.issues.filter(i => i.level === 'error'))}\nFix these points and return the complete creation.` });
  }
  if (last?.creation) return { kind, creation: last.creation, notes: last.notes, issues: last.issues, facts: last.facts, attempts: maxAttempts, model: last.model };
  throw new LlmError(`Generation failed after ${maxAttempts} attempts:\n${list(last?.issues ?? [])}`, 'invalid');
}
